import { useCallback, useMemo } from 'react';
import {
  parseDispatchText,
  sendParseFeedback,
  type ParseFeedback,
  type ParseTextResponse,
} from '../api/dispatchParse';
import { useDriverStateV2 } from './useDriverStateV2';
import { useHybridAuth } from './useHybridAuth';
import { getStoredSelectedCompanyId } from './userCompanies.shared';

/**
 * The dispatch text parser, with the signed-in member's token and company:
 * `parse` reads a text (and throws when it can't, so the caller falls back),
 * `feedback` posts how the call went and never throws.
 */
export function useDispatchParse() {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();

  const auth = useCallback(async () => {
    // An office member with no Driver row has no driver company; the stored
    // selection stands in, as it does for creating the call.
    const companyId = driver?.companyId || getStoredSelectedCompanyId();
    if (!companyId) throw new Error('Company ID not available');
    const token = await getAccessTokenSilently();
    return { token, companyId };
  }, [driver?.companyId, getAccessTokenSilently]);

  const parse = useCallback(
    async (text: string): Promise<ParseTextResponse> =>
      parseDispatchText(text, await auth()),
    [auth]
  );

  const feedback = useCallback(
    (body: ParseFeedback) => {
      void auth()
        .then((a) => sendParseFeedback(body, a))
        .catch(() => {
          // Fire and forget: a lost feedback line changes nothing for the
          // dispatcher.
        });
    },
    [auth]
  );

  return useMemo(() => ({ parse, feedback }), [parse, feedback]);
}
